import { useEffect, useState } from 'react';
import { useParams, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Printer, X } from 'lucide-react';
import { useAuth } from '../context/auth';
import { useQuoteStore } from '../stores/quoteStore';
import { useHubStore } from '../stores/hubStore';
import { findQuoteAndJobForPrintFromHub } from '../types/hub';
import type { Quote, ChildQuote } from '../types/quote';
import type { Job } from '../types/job';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/common/Button';
import PrintableBidView from '../components/quotes/PrintableBidView';

type HubPrintPayload = { quote: Quote | ChildQuote; job: Job };

export default function PrintableBidPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const [searchParams] = useSearchParams();
  const jobIdFromQuery = searchParams.get('jobId');
  const { user, loading: authLoading } = useAuth();
  const { pathname, search } = useLocation();
  const { t } = useTranslation(['quotes', 'common']);

  const fetchQuoteById = useQuoteStore(s => s.fetchQuoteById);
  const quoteFromStore = useQuoteStore(s => s.quotes.find(q => String(q.id) === String(quoteId)));

  const fetchHubContext = useHubStore(s => s.fetchHubContext);
  const clearHubContext = useHubStore(s => s.clearHubContext);

  const [fetchState, setFetchState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [hubPayload, setHubPayload] = useState<HubPrintPayload | null>(null);

  useEffect(() => {
    if (!quoteId || !user) return;
    let cancelled = false;

    if (jobIdFromQuery) {
      setFetchState('loading');
      setHubPayload(null);
      void (async () => {
        const ctx = await fetchHubContext(jobIdFromQuery);
        if (cancelled) return;
        const found = ctx ? findQuoteAndJobForPrintFromHub(ctx, quoteId) : null;
        setHubPayload(found);
        setFetchState('done');
      })();
      return () => {
        cancelled = true;
        clearHubContext();
      };
    }

    setFetchState('loading');
    void fetchQuoteById(quoteId).finally(() => {
      if (!cancelled) setFetchState('done');
    });
    return () => {
      cancelled = true;
    };
  }, [quoteId, user, jobIdFromQuery, fetchQuoteById, fetchHubContext, clearHubContext]);

  const quote = hubPayload?.quote ?? quoteFromStore;
  const job = hubPayload?.job ?? quoteFromStore?.job;

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: `${pathname}${search}` }} />;
  }

  if (!quoteId) {
    return <Navigate to="/projects" replace />;
  }

  if (fetchState === 'loading' || fetchState === 'idle') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingSpinner label={t('loading', { ns: 'common', defaultValue: 'Loading...' })} />
      </div>
    );
  }

  if (!quote || !job) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4 p-6">
        <p className="text-secondary font-ambit">
          {t('print.notFound', {
            ns: 'quotes',
            defaultValue: 'Soumission introuvable ou accès refusé.',
          })}
        </p>
        <Button variant="transparent-with-border" onClick={() => window.close()}>
          {t('common:form_actions.close', { ns: 'common', defaultValue: 'Close' })}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light print:bg-white py-6 print:py-0">
      {/* Toolbar (hidden on print) */}
      <div className="print:hidden max-w-[52rem] mx-auto px-4 md:px-8 flex items-center justify-between gap-3 mb-4">
        <button
          type="button"
          onClick={() => window.close()}
          className="flex items-center gap-2 text-secondary font-ambit text-sm hover:opacity-70"
          aria-label={t('common:form_actions.close', { ns: 'common', defaultValue: 'Close' })}
        >
          <X size={18} aria-hidden />
          <span>{t('common:form_actions.close', { ns: 'common', defaultValue: 'Close' })}</span>
        </button>
        <Button
          type="button"
          variant="primary"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 md:px-6"
        >
          <Printer size={18} aria-hidden />
          <span>{t('print.printButton', { ns: 'quotes', defaultValue: 'Imprimer' })}</span>
        </Button>
      </div>

      <PrintableBidView quote={quote} job={job} />
    </div>
  );
}
